import {
  SelectableObjectItem,
  SharingManagerButton,
} from '@readcloud/annotations';
import { colorOptions, TypeFromObjectSchema } from '@readcloud/common';
import {
  Button,
  ToggleButton,
} from '@readcloud/component-library/components/Base/Button';
import {
  Label,
  TextArea,
} from '@readcloud/component-library/components/Base/Form';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalTitle,
} from '@readcloud/component-library/components/Base/Modal';

import { ButtonWithConfirmation } from '@readcloud/component-library';
import { Cloud, User } from '@readcloud/data';
import { Form, FormikProps, withFormik } from 'formik';
import React from 'react';
import TwitterPicker from 'react-color/lib/components/twitter/Twitter';
import type { Schema } from 'yup';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  annotationFormNoteRequiredSchema,
  annotationFormSchema,
} from '../../../types/AnnotationFormSchemas';

import { Collapse } from '@readcloud/component-library';
import { Popover, PopoverContent, PopoverTrigger, Toggle } from '@readcloud/ui';
import { BsFillCircleFill } from 'react-icons/bs';

interface IAnnotationFormNoteRequired {
  note: string;
  color: string;
  shared?: boolean;
}

//import { variables } from '../../assets/ReaderVariables';
interface VisibilityControllerButtonsProps {
  forceClose?: boolean;
  users: User[];
  currentCloudId: string;
  clouds: Cloud[];
  selectedClouds: string[];
  selectedUsers: string[];
  onSelectedCloudsChange: (selectedClouds: string[]) => void;
  onSelectedUsersChange: (selectedUsers: string[]) => void;
}

interface VisibilityControllerButtonsState {
  selectedCloudsSet: Array<string>;
  selectedUsersSet: Array<string>;
  users: SelectableObjectItem[];
  clouds: SelectableObjectItem[];
}
const VisibilityControllerButtons: React.FC<
  VisibilityControllerButtonsProps
> = (props) => {
  const mapClouds = (clouds: Cloud[]) =>
    clouds
      .map((cloud) =>
        cloud
          ? {
              primaryText: cloud.name,
              secondaryText: cloud.institution ? cloud.institution : '',
              id: cloud.id,
            }
          : undefined
      )
      .filter((i) => !!i) as SelectableObjectItem[];

  const mapUsers = (users: User[]) =>
    users
      .map((user) =>
        user
          ? {
              avatarName: `${user.firstName} ${user.lastName}`,
              primaryText: `${user.firstName} ${user.lastName}`,
              secondaryText: user.email,
              id: user.id,
            }
          : undefined
      )
      .filter((i) => !!i) as SelectableObjectItem[];

  const [selectedCloudsSet, setSelectedCloudsSet] = React.useState<string[]>(
    props.selectedClouds
  );
  const [selectedUsersSet, setSelectedUsersSet] = React.useState<string[]>(
    props.selectedUsers
  );
  const [users, setUsers] = React.useState<SelectableObjectItem[]>(
    mapUsers(props.users)
  );
  const [clouds, setClouds] = React.useState<SelectableObjectItem[]>(
    mapClouds(props.clouds)
  );

  React.useEffect(() => {
    setSelectedUsersSet(props.selectedUsers);
  }, [props.selectedUsers]);

  React.useEffect(() => {
    setSelectedCloudsSet(props.selectedClouds);
  }, [props.selectedClouds]);

  React.useEffect(() => {
    setUsers(mapUsers(props.users));
  }, [props.users]);

  React.useEffect(() => {
    setClouds(mapClouds(props.clouds));
  }, [props.clouds]);

  const handleSelectedCloudsChange = (selectedClouds: Array<string>) => {
    props.onSelectedCloudsChange(selectedClouds);
  };
  const handleSelectedUsersChange = (selectedUsers: Array<string>) => {
    props.onSelectedUsersChange(selectedUsers);
  };

  return (
    <>
      {users && users.length ? (
        <SharingManagerButton
          iconLabel="Users"
          forceClose={props.forceClose}
          icon={<FontAwesomeIcon icon={'user'} />}
          title="Share with users"
          shareWith="Users"
          items={users}
          selectedItems={selectedUsersSet}
          onChange={handleSelectedUsersChange}
        />
      ) : null}
      {clouds && clouds.length ? (
        <>
          <div className="px-1" />
          <SharingManagerButton
            iconLabel="Clouds"
            forceClose={props.forceClose}
            icon={<FontAwesomeIcon icon={'cloud'} />}
            title="Share with Clouds"
            shareWith="Clouds"
            items={clouds}
            selectedItems={selectedCloudsSet}
            onChange={handleSelectedCloudsChange}
          />
        </>
      ) : null}
    </>
  );
};
const CancelButton = ({ onClick }: { onClick: () => void }) => (
  <Button variant="light" onClick={onClick}>
    Cancel
  </Button>
);

type AnnotationFormType = TypeFromObjectSchema<typeof annotationFormSchema>;
/* type AnnotationFormNoteRequiredType = TypeFromObjectSchema<
  typeof annotationFormNoteRequiredSchema
>; */

const defaultInitialValues = {
  note: '',
  shared: false,
  visibleInClouds: [],
  visibleToUsers: [],
  confirmOpen: false,
  previousValues: undefined,
  forceClose: false,
};

const privateIcon = <i className="fe fe-eye" />;
const styles = {
  checkboxContainer: {
    flexShrink: 0,
  },
};

export interface CommonProps {
  cloudId: string;
  ctaText: string;
  onClose: () => void;
  open: boolean;
  knownUsers: User[];
  knownClouds: Cloud[];
  onSuccess: (values: AnnotationFormType) => void;
  noteRequired?: boolean;
  initialValues: Partial<AnnotationFormType>;
  noColor?: boolean;
}
type Props = CommonProps;
type FormProps = FormikProps<AnnotationFormType> & CommonProps;

const InnerForm = (props: FormProps) => {
  const {
    setValues,
    setFieldValue,
    values,
    cloudId,
    ctaText,
    onClose,
    knownUsers,
    knownClouds,
    noColor,
    handleBlur,
    handleChange,
    touched,
    errors,
    dirty,
    isValid,
    validateForm,
  } = props;
  const [{ confirmOpen, previousValues, forceClose }, setState] =
    React.useState({
      confirmOpen: false,
      previousValues: undefined as any,
      forceClose: false,
    });
  const [colorPickerOpen, setColorPickerOpen] = React.useState<boolean>(false);
  React.useEffect(() => {
    validateForm(values);
  }, []);
  return (
    <Form className="rc-flex rc-flex-col">
      {confirmOpen && previousValues ? (
        <Modal isOpen={props.open}>
          <ModalTitle>Confirm visibility change</ModalTitle>
          <ModalBody>
            Removing all clouds and users will make this annotation private
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => {
                if (previousValues) {
                  setState((state) => ({
                    ...state,
                    confirmOpen: false,
                  }));
                  setValues({
                    ...previousValues,
                  });
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setState((state) => ({
                  ...state,
                  confirmOpen: false,
                  forceClose: true,
                }));
                setValues({
                  ...values,
                  shared: false,
                  visibleInClouds: [cloudId],
                });

                setTimeout(() => setFieldValue('forceClose', false));
              }}
            >
              Confirm
            </Button>
          </ModalFooter>
        </Modal>
      ) : null}
      <div className="rc-flex rc-items-center rc-justify-between rc-overflow-hidden">
        {values.annotatedText ? (
          <div className="annotation-block-quote">{values.annotatedText}</div>
        ) : (
          <div className="rc-p-2" />
        )}
        <div className="rc-flex rc-px-2" />
        {knownUsers.length || knownClouds.length ? (
          <>
            <div className="rc-flex rc-flex-row rc-justify-center rc-items-center">
              <Collapse isOpen={values.shared} timeout={250}>
                <div className="rc-flex rc-flex-row rc-px-2">
                  <VisibilityControllerButtons
                    forceClose={forceClose}
                    users={knownUsers}
                    currentCloudId={cloudId}
                    clouds={knownClouds}
                    selectedUsers={(values.visibleToUsers || []).filter(
                      (user): user is string => !!user
                    )}
                    selectedClouds={(values.visibleInClouds || []).filter(
                      (cloud): cloud is string => !!cloud
                    )}
                    onSelectedCloudsChange={(visibleInClouds) => {
                      if (
                        !visibleInClouds.length &&
                        !values.visibleToUsers?.length
                      ) {
                        setState((state) => ({
                          ...state,
                          confirmOpen: true,
                          previousValues: values,
                        }));
                        setValues({
                          ...values,
                          visibleInClouds,
                        });
                      } else {
                        setFieldValue('visibleInClouds', visibleInClouds);
                      }
                    }}
                    onSelectedUsersChange={(visibleToUsers) => {
                      if (
                        !visibleToUsers.length &&
                        !values.visibleInClouds?.length
                      ) {
                        setState((state) => ({
                          ...state,
                          confirmOpen: true,
                          previousValues: values,
                        }));
                        setValues({
                          ...values,
                          visibleToUsers,
                        });
                      } else {
                        setFieldValue('visibleToUsers', visibleToUsers);
                      }
                    }}
                  />
                </div>
              </Collapse>
              <label
                htmlFor="shared"
                className="rc-flex rc-flex-col rc-items-center"
              >
                <Toggle
                  className="rc-mb-0"
                  id={`shared`}
                  onPressedChange={(value) => {
                    setFieldValue('shared', value);
                  }}
                  type="button"
                  name={`shared`}
                  value={'shared'}
                  key={`shared`}
                  defaultPressed={values.shared}
                  pressed={values.shared}
                >
                  <FontAwesomeIcon icon={'eye'} />
                </Toggle>
                <small
                  style={{ minWidth: '2.6rem' }}
                  className="rc-text-center"
                >
                  {values.shared ? 'Shared' : 'Private'}
                </small>
              </label>
            </div>
          </>
        ) : null}
      </div>

      <div className="rc-py-2" />
      <Label htmlFor="note">Note</Label>
      <TextArea
        onBlur={handleBlur}
        onChange={handleChange}
        name="note"
        value={values.note}
      />
      {touched.note && errors.note && (
        <small className="rc-text-red-500">{errors.note}</small>
      )}

      <div className="rc-py-2" />
      <div className="rc-flex rc-justify-between rc-w-full rc-items-center">
        {!noColor ? (
          <Popover
            open={colorPickerOpen}
            onOpenChange={() => setColorPickerOpen((s) => !s)}
          >
            <PopoverTrigger asChild>
              <Button variant="light">
                <BsFillCircleFill color={values.color} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="rc-z-[2001] p-0 border-0" align="start">
              <TwitterPicker
                color={values.color}
                colors={[...Object.values(colorOptions), '#0000ff']}
                onChangeComplete={(color) => {
                  setFieldValue('color', color.hex);
                  setColorPickerOpen(false);
                }}
              />
            </PopoverContent>
          </Popover>
        ) : null}
        <div className="rc-flex">
          <div className="rc-px-1" />
          {dirty ? (
            <ButtonWithConfirmation
              confirmVariant="danger"
              onConfirm={onClose}
              title="Cancel Annotation"
              message="Your progress will be lost if you cancel, are you sure?"
              renderButton={
                <Button variant="light" onClick={onClose}>
                  Cancel
                </Button>
              }
            />
          ) : (
            <Button variant="light" onClick={onClose}>
              Cancel
            </Button>
          )}

          <div className="rc-px-1" />
          <Button
            disabled={!isValid}
            variant="primary"
            onClick={(e) => props.onSuccess(values)}
          >
            {ctaText}
          </Button>
        </div>
      </div>
    </Form>
  );
};

type OuterProps = {
  validationSchema: Schema<any>;
  initialValues: Partial<AnnotationFormType>;
} & CommonProps;

export const AnnotationFormInner = withFormik<OuterProps, AnnotationFormType>({
  displayName: 'AnnotationModalForm',
  handleSubmit: (values, formik) => {
    formik.props.onSuccess(values);
  },
  mapPropsToValues: (props) => {
    return props.initialValues as any;
  },
  validationSchema: (props: OuterProps) => props.validationSchema,
  isInitialValid: true,
})(InnerForm);

export const AnnotationModal = (props: Props) => {
  const {
    open,
    onClose,
    initialValues,
    onSuccess,
    cloudId,
    knownClouds,
    knownUsers,
    ctaText,
  } = props;

  const formInitialValues = React.useMemo(
    () => ({
      ...defaultInitialValues,
      ...initialValues,
    }),
    [initialValues]
  );
  return (
    <Modal backdrop isOpen={open} toggle={onClose}>
      <ModalBody>
        <AnnotationFormInner
          onClose={onClose}
          cloudId={cloudId}
          open={open}
          knownClouds={knownClouds}
          knownUsers={knownUsers}
          ctaText={ctaText}
          validationSchema={
            props.noteRequired
              ? annotationFormNoteRequiredSchema
              : annotationFormSchema
          }
          onSuccess={onSuccess}
          initialValues={
            {
              color: '#fff100',
              ...formInitialValues,
              visibleInClouds:
                !formInitialValues.visibleInClouds?.length &&
                !formInitialValues.visibleToUsers?.length
                  ? [cloudId]
                  : formInitialValues.visibleInClouds,
            } as any
          }
        />
      </ModalBody>
    </Modal>
  );
};
